import React, { useState, useRef, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import TableUp, {
    defaultCustomSelect,
    TableMenuContextmenu,
    TableResizeLine,
    TableSelection,
    TableAlign,
} from 'quill-table-up';
import 'quill-table-up/index.css';
import 'quill-table-up/table-creator.css';

// ── One-time Quill registrations ──────────────────────────────────────────────

const Quill = ReactQuill.Quill;

// Register callout block blots
const Block = Quill.import('blots/block');
['tip', 'note', 'info', 'warning'].forEach(type => {
    class CalloutBlock extends Block {
        static create(value) {
            const node = super.create(value);
            node.classList.add('callout');
            return node;
        }
    }
    CalloutBlock.blotName = `callout_${type}`;
    CalloutBlock.tagName = 'div';
    CalloutBlock.className = `callout-${type}`;
    Quill.register(CalloutBlock, true);
});

// Register the TableUp module
Quill.register({ [`modules/${TableUp.moduleName}`]: TableUp }, true);

// ── Static config ─────────────────────────────────────────────────────────────

const CALLOUT_TYPES = ['tip', 'note', 'info', 'warning'];

const calloutButtons = [
    { type: 'tip',     label: '💡 Tip',     border: '#2da44e', bg: '#f0fff4', text: '#155724' },
    { type: 'note',    label: '📝 Note',    border: '#e3a008', bg: '#fffde7', text: '#7d6608' },
    { type: 'info',    label: 'ℹ️ Info',    border: '#1a73e8', bg: '#e8f0fe', text: '#1558d6' },
    { type: 'warning', label: '⚠️ Warning', border: '#e65100', bg: '#fff3e0', text: '#bf360c' },
];

// Table tool appears in the standard Quill toolbar via the table-up picker
const modules = {
    toolbar: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ font: [] }],
        [{ align: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        [{ [TableUp.toolName]: [] }],
        ['clean'],
    ],
    // TableUp module — grid picker + resize + selection + right-click menu
    [TableUp.moduleName]: {
        customSelect: defaultCustomSelect,
        modules: [
            { module: TableAlign },
            { module: TableResizeLine },
            { module: TableSelection },
            { module: TableMenuContextmenu },
        ],
    },
};

// ── Preview helper ────────────────────────────────────────────────────────────

/**
 * Stamps custom classes onto table elements in an HTML string so the preview
 * can be styled independently of Quill's internal class names.
 *
 *  table → db-table
 *  tr    → db-row
 *  td    → db-cell
 *  th    → db-header
 */
export function processPreviewHtml(html) {
    if (!html) return html;
    const div = document.createElement('div');
    div.innerHTML = html;
    div.querySelectorAll('table').forEach(el => el.classList.add('db-table'));
    div.querySelectorAll('tr').forEach(el => el.classList.add('db-row'));
    div.querySelectorAll('td').forEach(el => el.classList.add('db-cell'));
    div.querySelectorAll('th').forEach(el => el.classList.add('db-header'));
    return div.innerHTML;
}

/**
 * Renders processed HTML and injects a working COPY button into every
 * .ql-code-block-container found in the output.
 */
export function PreviewContent({ html, className }) {
    const ref = useRef(null);

    useEffect(() => {
        if (!ref.current) return;

        // dangerouslySetInnerHTML already replaced the DOM — inject fresh buttons
        ref.current.querySelectorAll('.ql-code-block-container').forEach(container => {
            if (container.querySelector('.code-copy-btn')) return; // already injected

            const btn = document.createElement('button');
            btn.textContent = 'COPY';
            btn.className = 'code-copy-btn';

            btn.addEventListener('click', () => {
                const text = Array.from(container.querySelectorAll('div'))
                    .map(d => d.innerText ?? d.textContent)
                    .join('\n');
                navigator.clipboard.writeText(text).then(() => {
                    btn.textContent = 'COPIED!';
                    setTimeout(() => { btn.textContent = 'COPY'; }, 2000);
                });
            });

            container.appendChild(btn);
        });
    }, [html]);

    return (
        <div
            ref={ref}
            className={className}
            dangerouslySetInnerHTML={{ __html: processPreviewHtml(html) }}
        />
    );
}

// ── Component ─────────────────────────────────────────────────────────────────

function RichTextEditor({ value, onChange, placeholder = 'Start typing...' }) {
    const [editorTab, setEditorTab] = useState('visual');
    const quillRef = useRef(null);

    const getPlainText = (html) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = html;
        return tmp.textContent || tmp.innerText || '';
    };

    const insertCallout = (type) => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const range = quill.getSelection(true);
        if (!range) return;

        const currentFormat = quill.getFormat(range.index, range.length || 1);
        if (currentFormat[`callout_${type}`]) {
            quill.format(`callout_${type}`, false, 'user');
        } else {
            CALLOUT_TYPES.forEach(t => {
                if (t !== type && currentFormat[`callout_${t}`]) {
                    quill.format(`callout_${t}`, false, 'user');
                }
            });
            quill.format(`callout_${type}`, true, 'user');
        }
    };

    return (
        <div>
            {/* Visual / HTML / Text tab switcher */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '8px' }}>
                {['visual', 'html', 'text'].map((tab) => (
                    <button
                        key={tab}
                        type="button"
                        onClick={() => setEditorTab(tab)}
                        style={{
                            padding: '3px 12px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            background: editorTab === tab ? '#fff' : 'transparent',
                            fontWeight: editorTab === tab ? '600' : 'normal',
                            boxShadow: editorTab === tab ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            cursor: 'pointer',
                            fontSize: '13px',
                            textTransform: 'capitalize',
                        }}
                    >
                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                    </button>
                ))}
            </div>

            {editorTab === 'visual' && (
                <>
                    {/* Callout buttons bar */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', border: '1px solid #ccc', borderBottom: 'none', borderRadius: '4px 4px 0 0', background: '#f8f9fa', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '11px', fontWeight: '700', color: '#888', letterSpacing: '0.8px', marginRight: '4px' }}>CALLOUTS</span>
                        {calloutButtons.map(({ type, label, border, bg, text }) => (
                            <button
                                key={type}
                                type="button"
                                onClick={() => insertCallout(type)}
                                style={{
                                    padding: '3px 10px',
                                    border: `1px solid ${border}`,
                                    borderRadius: '4px',
                                    background: bg,
                                    color: text,
                                    fontWeight: '500',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={value}
                        onChange={onChange}
                        modules={modules}
                        placeholder={placeholder}
                        style={{ borderRadius: '0 0 4px 4px' }}
                    />
                </>
            )}

            {editorTab === 'html' && (
                <textarea
                    className="form-control"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    style={{ minHeight: '800px', fontFamily: 'monospace', fontSize: '13px' }}
                />
            )}

            {editorTab === 'text' && (
                <textarea
                    className="form-control"
                    value={getPlainText(value)}
                    readOnly
                    style={{ minHeight: '800px', fontSize: '13px' }}
                />
            )}
        </div>
    );
}

export default RichTextEditor;
